// domain/akunkas — saldo per AKUN KAS (tunai laci · tiap rekening bank · QRIS) dan penganalisa
// selisih tutup kasir. MURNI: tak menyentuh basis data.
//
// Kenapa per akun, bukan satu pot "kas masuk/keluar" (keputusan Apo 22 Jul): hanya saldo per
// akun yang bisa dicocokkan ke bukti nyata — laci dihitung fisik, rekening dicocokkan mutasi
// bank, QRIS dicocokkan laporan settlement. Selisih pada satu angka gabungan tak bisa
// ditelusuri ke mana pun.

export type JenisAkun = 'tunai' | 'bank' | 'qris' | 'ewallet';

export interface AkunKas {
  id: number;
  nama: string;                  // yang dibaca kasir & disimpan di cara bayar tiket
  jenis: JenisAkun;
  outlet: string | null;         // null = dipakai semua cabang
  saldoAwal: number;
  tglAwal: string | null;
  aktif: boolean;
  urut: number;
  kodeAkun?: string | null;      // nomor akun COA — diisi saat pembukuan penuh dipasang
}

export type JenisMutasi = 'beban' | 'setoran' | 'pencairan' | 'modal' | 'prive' | 'supplier' | 'penyesuaian' | 'lainnya';

export interface MutasiKas {
  id?: number;
  tgl: string;                   // yyyy-mm-dd (hari bisnis lokal)
  akunId: number;
  arah: 'masuk' | 'keluar';
  jumlah: number;
  jenis: JenisMutasi;
  catatan?: string | null;
}

/**
 * Akun yang dipakai sebuah pembayaran. Cara bayar disimpan sebagai TEKS pada tiket
 * (mis. "Transfer BCA"), jadi pencocokan bertingkat:
 *   1. nama akun persis  → itulah akunnya (dua rekening bank bisa dibedakan)
 *   2. nama akun disebut di dalam teksnya
 *   3. jatuh ke jenis: "tunai"→tunai, "qris"→qris, "transfer/bank"→bank pertama
 * Tak ketemu → null (JANGAN ditebak jadi tunai: uang yang belum jelas asalnya harus terlihat
 * sebagai belum jelas, supaya kasir memperbaiki notanya).
 */
export function akunUntukCara(cara: string | null | undefined, akun: AkunKas[], outlet?: string | null): AkunKas | null {
  const teks = (cara ?? '').trim().toLowerCase();
  if (!teks) return null;
  const pakai = akun.filter((a) => a.aktif && (!outlet || !a.outlet || a.outlet === outlet));
  const urut = [...pakai].sort((a, b) => a.urut - b.urut || a.id - b.id);

  const persis = urut.find((a) => a.nama.trim().toLowerCase() === teks);
  if (persis) return persis;
  const sebagian = urut.find((a) => a.nama.trim().length > 2 && teks.includes(a.nama.trim().toLowerCase()));
  if (sebagian) return sebagian;

  const jenis: JenisAkun | null =
    /tunai|cash/.test(teks) ? 'tunai'
    : /qris/.test(teks) ? 'qris'
    : /transfer|bank|va|virtual/.test(teks) ? 'bank'
    : /wallet|ovo|gopay|dana|shopeepay/.test(teks) ? 'ewallet' : null;
  return jenis ? (urut.find((a) => a.jenis === jenis) ?? null) : null;
}

export interface BarisAkun {
  akun: AkunKas;
  saldoAwal: number;             // saldo sebelum hari ini
  masukTiket: number;            // pembayaran pelanggan hari ini
  masukLain: number;             // setoran masuk, pencairan QRIS, modal
  keluar: number;                // beban, refund, setoran keluar, prive, bayar supplier
  seharusnya: number;            // saldoAwal + masuk − keluar
}

/**
 * Susun baris tutup kasir per akun. SELALU mengembalikan semua akun aktif — termasuk yang
 * nihil hari itu (permintaan Apo): kasir perlu melihat "Tunai Rp 0" secara sadar, bukan
 * kehilangan barisnya dan mengira tak perlu dihitung.
 */
export function barisAkun(
  akun: AkunKas[],
  masukTiketPerAkun: Map<number, number>,
  keluarTiketPerAkun: Map<number, number>,
  mutasi: MutasiKas[],
  saldoAwalPerAkun: Map<number, number>,
): BarisAkun[] {
  return [...akun]
    .filter((a) => a.aktif)
    .sort((a, b) => a.urut - b.urut || a.id - b.id)
    .map((a) => {
      const mt = masukTiketPerAkun.get(a.id) ?? 0;
      const kt = keluarTiketPerAkun.get(a.id) ?? 0;
      const mv = mutasi.filter((m) => m.akunId === a.id);
      const masukLain = mv.filter((m) => m.arah === 'masuk').reduce((s, m) => s + m.jumlah, 0);
      const keluarLain = mv.filter((m) => m.arah === 'keluar').reduce((s, m) => s + m.jumlah, 0);
      const saldoAwal = saldoAwalPerAkun.get(a.id) ?? 0;
      const keluar = kt + keluarLain;
      return { akun: a, saldoAwal, masukTiket: mt, masukLain, keluar, seharusnya: saldoAwal + mt + masukLain - keluar };
    });
}

// ── Penganalisa selisih ───────────────────────────────────────────────────────
export interface KonteksSelisih {
  selisih: number;               // fisik − seharusnya (minus = uang kurang)
  jenisAkun: JenisAkun;
  belumDicatat: number;          // uang masuk tanpa metode yang jelas
  tempoBaru: number;             // piutang baru hari ini
  qrisBelumCair: number;         // saldo mengendap di akun QRIS
  bebanBelumDibayar: number;     // beban tercatat tapi belum dipilih sumber uangnya
  setoranHariIni: number;
}
export interface DugaanSelisih { tingkat: 'baik' | 'perhatian' | 'bahaya'; judul: string; pesan: string }

/**
 * Menerangkan selisih dengan aturan, bukan tebakan: tiap dugaan hanya muncul bila ada angka
 * yang MEMANG cocok dengan selisihnya. Ditulis seperti asisten yang menunjuk berkas — kasir
 * bisa langsung memeriksa, bukan disuruh menerka.
 */
export function analisaSelisih(k: KonteksSelisih): DugaanSelisih[] {
  const out: DugaanSelisih[] = [];
  const s = Math.round(k.selisih);
  const rp = (v: number): string => 'Rp ' + Math.abs(Math.round(v)).toLocaleString('id-ID');
  const mirip = (v: number): boolean => v > 0 && Math.abs(Math.abs(s) - v) <= Math.max(1000, v * 0.02);

  if (s === 0) {
    out.push({ tingkat: 'baik', judul: 'Cocok', pesan: 'Hitungan fisik sama dengan catatan. Tak ada yang perlu ditelusuri.' });
    return out;
  }

  const arah = s < 0 ? 'KURANG' : 'LEBIH';
  out.push({
    tingkat: Math.abs(s) > 100_000 ? 'bahaya' : 'perhatian',
    judul: `Uang ${arah} ${rp(s)}`,
    pesan: s < 0
      ? 'Uang yang ada lebih sedikit daripada catatan. Telusuri sebelum kasir pulang — semakin lama semakin sulit diingat.'
      : 'Uang yang ada lebih banyak daripada catatan. Biasanya ada penerimaan yang belum dicatat, bukan rezeki.',
  });

  if (mirip(k.belumDicatat)) {
    out.push({ tingkat: 'perhatian', judul: 'Cocok dengan pembayaran tanpa metode', pesan: `Ada ${rp(k.belumDicatat)} diterima tanpa keterangan tunai/transfer/QRIS — besarnya mendekati selisih ini. Perbaiki cara bayar notanya, biasanya selisih langsung hilang.` });
  }
  if (mirip(k.setoranHariIni)) {
    out.push({ tingkat: 'perhatian', judul: 'Cocok dengan setoran hari ini', pesan: `Setoran hari ini ${rp(k.setoranHariIni)} — periksa apakah uangnya sudah keluar laci tetapi belum tercatat, atau tercatat dua kali.` });
  }
  if (mirip(k.bebanBelumDibayar)) {
    out.push({ tingkat: 'perhatian', judul: 'Cocok dengan beban yang belum ditentukan sumbernya', pesan: `Ada beban ${rp(k.bebanBelumDibayar)} tanpa pilihan "dibayar dari". Kalau uangnya diambil dari laci, pilih Tunai — selisihnya akan tertutup.` });
  }
  if (k.jenisAkun === 'qris' && k.qrisBelumCair > 0) {
    out.push({ tingkat: 'perhatian', judul: 'QRIS belum cair', pesan: `${rp(k.qrisBelumCair)} masih mengendap di akun QRIS. Dana QRIS biasanya cair H+1 — pindahkan ke rekening setelah masuk, jangan dihitung sebagai uang laci.` });
  }
  if (s < 0 && k.tempoBaru > 0 && mirip(k.tempoBaru)) {
    out.push({ tingkat: 'perhatian', judul: 'Cocok dengan piutang baru hari ini', pesan: `Ada ${rp(k.tempoBaru)} unit keluar dengan tempo. Piutang BUKAN uang masuk — pastikan tak ada nota tempo yang keliru ditandai lunas.` });
  }
  if (out.length === 1) {
    out.push({ tingkat: 'perhatian', judul: 'Belum ada angka yang cocok', pesan: 'Tak ada catatan hari ini yang besarnya menyerupai selisih. Periksa: kembalian salah hitung, uang pribadi tercampur, nota belum diinput, atau uang keluar tanpa catatan.' });
  }
  return out;
}
